<template>
  <div :class="$style.table">
    <div v-if="title" :class="$style.table__header">
      <h2 :class="$style.table__title">
        {{ title }}
      </h2>
    </div>

    <template v-if="$slots.filters">
      <slot name="filters" />
    </template>

    <div v-if="showLoadingMessage" :class="$style.table__loading">
      Searching for data...
    </div>
    <template v-else>
      <div
        v-for="(row, index) in paginatedRows"
        :key="index"
        :class="$style.table__card"
      >
        <div
          v-for="(header, i) in headers"
          :key="i"
          :class="$style.table__row"
        >
          <span :class="$style.table__label">
            {{ header.title }}:
          </span>
          <span :class="$style.table__value">
            {{ row[header.key] }}
          </span>
        </div>
      </div>
      <div v-if="enablePagination && totalPages > 1" :class="$style.table__pagination">
        <button 
          @click="changePage(currentPage - 1)"
          :disabled="currentPage === 1"
        >
          ◀
        </button>
  
        <span>Page {{ currentPage }} of {{ totalPages }}</span>
  
        <button 
          @click="changePage(currentPage + 1)"
          :disabled="currentPage === totalPages"
        >
          ▶
        </button>
      </div>
    </template>
  </div>
</template>

<script setup>
  import { computed, ref, watch } from 'vue';

  const props = defineProps({
    title: String,
    headers: Array,
    rows: Array,
    loadingData: {
      type: Boolean,
      default: true
    },
    enablePagination: {
      type: Boolean,
      default: true
    },
    itemsPerPage: {
      type: Number,
      default: 5
    }
  });

  const currentPage = ref(1);
  const showLoadingMessage = computed(() => {
    return props.loadingData;
  });
  const totalPages = computed(() => {
    return Math.ceil(props.rows.length / props.itemsPerPage);
  });

  const paginatedRows = computed(() => {
    if(!props.enablePagination) return props.rows;

    const start = (currentPage.value - 1) * props.itemsPerPage;
    return props.rows.slice(start, start + props.itemsPerPage);
  });

  function changePage(page) {
    if (page >= 1 && page <= totalPages.value) {
      currentPage.value = page;
    }
  }

  watch(() => props.rows, () => {
    currentPage.value = 1;
  });
</script>

<style module lang="scss">
  .table {
    display: flex;
    flex-direction: column;
    gap: 16px;

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    &__title {
      margin: 0;
      color: #D9D9D9;
      font-size: 26px;
      font-weight: 600;
      line-height: 36px;

      @include respond-to(null, 'xs') {
        font-size: 22px !important;
      }
    }

    &__card {
      padding: 16px;
      border-radius: 8px;
      background-color: rgba(45, 45, 45, 0.35);
      border: 1px solid rgba(83, 83, 83, 0.25);
    }

    &__row {
      display: flex;
      margin-bottom: 8px;
      justify-content: space-between;
    }

    &__label {
      gap: 8px;
      display: flex;
      font-weight: bold;
      align-items: center;
      font-size: 12px;
    }

    &__icon {
      width: 16px;
      height: 16px;
      color: #C1C1C1;
    }

    &__value {
      color: #D9D9D9;
      font-size: 14px;
    }

    &__pagination {
      gap: 16px;
      display: flex;
      align-items: center;
      justify-content: center;

      button {
        cursor: pointer;
        color: #C1C1C1;
        padding: 8px 16px;
        border-radius: 4px;
        background-color: rgba(124, 249, 92, 0.15);
        border: 1px solid rgba(124, 249, 92, 0.25);

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }
    }

    &__loading {
      font-size: 18px;
      font-weight: 500;
      color: #C1C1C1;
      text-align: center;
      margin-top: 20px;
    }
  }
</style>
